'use client';

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react';

const AuthContext = createContext<any>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }

  return context;
};

export const AuthProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [profileLoading, setProfileLoading] = useState(true);

  const loadSession = useCallback(async () => {
    setLoading(true);
    setProfileLoading(true);

    try {
      const response = await fetch('/api/auth/me', {
        method: 'GET',
        credentials: 'include',
        cache: 'no-store',
      });

      if (!response.ok) {
        setUser(null);
        setSession(null);
        setUserProfile(null);
        return null;
      }

      const data = await response.json();

      setUser(data.user ?? null);
      setUserProfile(data.profile ?? null);

      const localSession = data.user
        ? { user: data.user }
        : null;

      setSession(localSession);

      return data;
    } catch {
      setUser(null);
      setSession(null);
      setUserProfile(null);
      return null;
    } finally {
      setLoading(false);
      setProfileLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  const signIn = async (
    email: string,
    password: string,
    remember = false
  ) => {
    const response = await fetch('/api/auth/login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        email,
        password,
        remember,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data?.error || 'Error al iniciar sesión');
    }

    await loadSession();

    return data;
  };

  const signOut = async () => {
    const response = await fetch('/api/auth/logout', {
      method: 'POST',
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Error al cerrar sesión');
    }

    setUser(null);
    setSession(null);
    setUserProfile(null);
  };

  const getCurrentUser = async () => {
    const response = await fetch('/api/auth/me', {
      method: 'GET',
      credentials: 'include',
      cache: 'no-store',
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();

    return data.user ?? null;
  };

  const getUserProfile = async () => {
    const response = await fetch('/api/auth/me', {
      method: 'GET',
      credentials: 'include',
      cache: 'no-store',
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();

    return data.profile ?? null;
  };

  const signUp = async () => {
    throw new Error(
      'El registro público de usuarios no está habilitado.'
    );
  };

  const isEmailVerified = () => {
    return true;
  };

  const value = {
    user,
    session,
    loading,
    userProfile,
    profileLoading,
    role: userProfile?.role ?? null,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    refreshSession: loadSession,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
